import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Input, Spinner, Icon } from '@iq/react-components';

import { getActiveSite } from '../../../bundles/sites';
import {
  getVisualizations,
  getVisualizationsLoaded,
  resetVisualizations,
  upsertVisualization,
  requestDeleteVisualization,
  saveVisualization,
  bulkUpsertVisualization,
} from '../../../bundles/visualizations';
import { getStaticComponents } from '../../../bundles/components';
import { setActiveComponentId } from '../../../bundles/application';
import {
  getSiteVariablesUpdatedAt,
  getSourcesLoaded,
  requestSources,
} from '../../../bundles/sources';

import Heading from '../../Heading';
import VisualizationEditor from '../../VisualizationEditor';
import { useEventListener, toKebabTitleCase } from '../../../utils';
import ListItem from '../../ListItem';
import { visualizations as vizMappings } from '../../Visualization';
import { DropdownMenu, DropdownTrigger } from '../../DropdownMenu';
import VisualizationSettingsModal from './components/VisualizationSettingsModal';
import LabelWithTooltip from '../../LabelWithTooltip';

const VisualizationView = ({ wizardType }) => {
  const dispatch = useDispatch();

  const site = useSelector(getActiveSite);
  const visualizations = useSelector(getVisualizations);
  const visualizationNames = visualizations.map((v) => v.name.toLowerCase());
  const loaded = useSelector(getVisualizationsLoaded);
  const components = useSelector(getStaticComponents);
  const siteVariablesUpdatedAt = useSelector(getSiteVariablesUpdatedAt);
  const sourcesLoaded = useSelector(getSourcesLoaded);

  const [updating, setUpdating] = useState(null);
  const [filterValue, setFilterValue] = useState('');
  const [selectedVisualizations, setSelectedVisualizations] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [showSettingsForm, setShowSettingsForm] = useState(false);

  useEffect(() => {
    if (site.id) {
      if (!siteVariablesUpdatedAt || sourcesLoaded !== site.id) {
        dispatch(requestSources({ siteId: site.id, withVariables: true }));
      }
      dispatch(setActiveComponentId(null));
    }
  }, [site.id]);

  useEffect(() => {
    setSelectedVisualizations([]);
  }, [visualizations]);

  useEventListener('keyup', (event) => {
    if (event.keyCode === 27) {
      setFilterValue('');
    }
  });

  const preFilteredVisualizations = useMemo(
    () => visualizations.filter((v) => v.id && !v.id.includes('temporary')),
    [visualizations]
  );
  const filteredVisualizations = useMemo(() => {
    const filters = filterValue.trim().split(' ');
    setAllSelected(false);
    const filteredVals = preFilteredVisualizations
      .filter((v) =>
        filters.every(
          (filter) =>
            v.name.toLowerCase().includes(filter.toLowerCase()) ||
            v.type.toLowerCase().includes(filter.toLowerCase())
        )
      )
      .map((viz) => {
        viz.selected = selectedVisualizations.map((v) => v.id).includes(viz.id);
        const compIds = (viz.relations || [])
          .filter((relation) => relation.entity_type?.includes('component'))
          .map((relation) => relation.entity_id);
        const relatedComponents = compIds
          .map((id) => components.find((c) => c.id === id))
          .filter(Boolean);
        return { ...viz, components: relatedComponents };
      });
    if (
      filteredVals.length > 0 &&
      filteredVals.filter((v) => v.selected).length === filteredVals.length
    )
      setAllSelected(true);
    setSelectedVisualizations(filteredVals.filter((v) => v.selected));
    return filteredVals;
  }, [filterValue, preFilteredVisualizations, components]);

  const menuOptions = ['None', ...(filteredVisualizations || []).map((_, index) => index + 1)];

  const toggleAllSelected = () => {
    if (allSelected) {
      for (const v of filteredVisualizations) v.selected = false;
      setSelectedVisualizations([]);
    } else {
      for (const v of filteredVisualizations) v.selected = true;
      setSelectedVisualizations(filteredVisualizations);
    }
    setAllSelected(!allSelected);
  };

  const handleOnClickCreate = () => {
    const timestamp = Date.now();
    const temporaryId = `temporary-${timestamp}`;
    dispatch(
      upsertVisualization(site.id, {
        id: temporaryId,
        site: site.id,
        org: site.org,
        name: `New visualization ${timestamp}`,
        type: 'graph',
        relations: [],
        variables: [],
        configuration: {},
        isTemplate: false,
      })
    );
    setUpdating(temporaryId);
  };

  const handleCloseSettingsModal = useCallback(
    () => setShowSettingsForm(false),
    [setShowSettingsForm]
  );

  const handleSubmitSettingsForm = useCallback(({ priority }) => {
    const update = {
      ids: selectedVisualizations.map((v) => v.id),
      priority: priority || 'none',
      site: site.id,
      org: site.org,
    };
    dispatch(bulkUpsertVisualization(update));
    for (const v of filteredVisualizations) v.selected = false;
    setSelectedVisualizations([]);
    setShowSettingsForm(false);
  });

  const handleCloseVisulizationClick = () => {
    setUpdating(null);
    dispatch(resetVisualizations());
  };

  const wizClass = wizardType ? `--${wizardType}` : '';
  const isWizardView = Boolean(wizardType);
  const actionColumnWidth = isWizardView ? 5 : 7;

  const onSelectVisualization = (visualization) => {
    const index = filteredVisualizations.findIndex((s) => s.id === visualization.id);
    filteredVisualizations[index].selected = !filteredVisualizations[index].selected;

    if (selectedVisualizations.some((f) => f.id === visualization.id)) {
      setSelectedVisualizations(selectedVisualizations.filter((f) => f.id !== visualization.id));
    } else {
      setSelectedVisualizations([...selectedVisualizations, visualization]);
    }
    if (filteredVisualizations.filter((v) => v.selected).length === filteredVisualizations.length) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  };

  const getListItemColumns = (visualization) => {
    const linkedComponentsLabel = visualization.components
      .map((component) => `${component?.name} (${component?.itemDesignation})`)
      .join(', ');

    const linkedComponentsTooltip =
      visualization.components.length > 0 ? (
        <>
          {visualization.components.map((c) => (
            <div key={c.id}>{`${c?.name} (${c?.itemDesignation})`}</div>
          ))}
        </>
      ) : null;

    const cols = [
      <div
        key={`${visualization.id}-col-1`}
        className="table-cell select-column"
      >
        <Button design="text">
          <Icon
            className={`checkbox ${visualization.selected ? 'checked' : ''}`}
            icon={`${visualization.selected ? 'he-checkbox-selected' : 'he-checkbox'}`}
            size="s"
            onClick={(event) => {
              event.stopPropagation();
              onSelectVisualization(visualization);
            }}
          />
        </Button>
      </div>,
      <>
        {visualization.type ? (
          <Icon
            icon={vizMappings.find((m) => m.type === visualization?.type)?.icon}
            size="s"
          />
        ) : null}
        <LabelWithTooltip name={visualization.name} />
      </>,
      <LabelWithTooltip
        key={`${visualization.id}-col-3`}
        name={
          visualization.type === 'duval' ? 'Duval-Triangle' : toKebabTitleCase(visualization?.type)
        }
      />,
      <DropdownMenu
        key={`${visualization.id}-col-4`}
        trigger={
          <DropdownTrigger
            icon="he-down"
            className="dropdown-font"
            label={
              visualization.configuration?.priority === 'none'
                ? 'None'
                : (visualization.configuration?.priority ?? 'None')
            }
            slim
          />
        }
        menuOptions={menuOptions.map((t) => ({
          label: t,
          value: t === 'None' ? 'none' : t,
          onSelect: (selection) =>
            dispatch(
              saveVisualization({
                ...visualization,
                configuration: { ...visualization.configuration, priority: selection.value },
              })
            ),
        }))}
        styles={{ container: { width: 'auto', minWidth: isWizardView ? '3.5rem' : '6rem' } }}
        menuShouldEscapeOverflow
      />,
      <div
        key={`${visualization.id}-col-5`}
        className="ellipsed-text"
      >
        <LabelWithTooltip
          name={linkedComponentsLabel}
          tooltip={linkedComponentsTooltip}
          forceTooltip={visualization.components.length > 0}
        />
      </div>,
    ];

    return cols;
  };

  const headers = [
    {}, // For select column
    { sortKey: 'name', label: 'Name' },
    { sortKey: 'type', label: 'Type' },
    { sortKey: 'priority', label: 'Priority' },
    { sortKey: undefined, label: 'Linked components' },
  ];

  const headerColumns = headers.map((h, index) => {
    const key = `${h.sortKey || h.label}-${index}`;
    if (!h.sortKey) {
      return <div key={key}>{h.label}</div>;
    }
    return (
      <Button
        key={key}
        iconPosition="right"
        activity="secondary"
      >
        {h.label}
      </Button>
    );
  });

  const colWidths = {
    type: 'percent',
    widths: isWizardView ? ['-', 20, 20, 15, 45] : ['-', 20, 15, 15, 50],
  };

  const manageVisualizationsBodyClassName = [
    'manage-visualizations-body',
    isWizardView ? 'manage-visualizations-body--wizard' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const listContainerClassName = ['list-container', isWizardView ? 'list-container--wizard' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div
        className={`visualizations-heading-row${isWizardView ? ' visualizations-heading-row--wizard' : ''}`}
      >
        <Heading
          contentLeft={
            <div
              className={`visualizations-header${isWizardView ? ' visualizations-header--wizard' : ''}`}
            >
              <div className="title">Visualizations</div>
              {preFilteredVisualizations.length > 0 && (
                <Input
                  type="text"
                  onChange={(event) => setFilterValue(event.target.value)}
                  value={filterValue}
                  placeholder="Search..."
                />
              )}
            </div>
          }
          contentRight={
            <Button
              className={`button-create${wizardType ? wizClass : ''}`}
              onClick={handleOnClickCreate}
            >
              Add visualization
            </Button>
          }
        />
      </div>

      {loaded && site ? (
        <div className={manageVisualizationsBodyClassName}>
          <p className="visualizations">
            {`Manage visualizations (${filteredVisualizations?.length})`}
          </p>

          <div className="selection-toggler">
            <label
              className="select-all-label"
              onClick={toggleAllSelected}
            >
              <Icon
                className={`select-all ${allSelected ? 'checked' : ''}`}
                icon={`${allSelected ? 'he-checkbox-selected' : 'he-checkbox'}`}
                size="s"
              />
              Select all
            </label>
            {selectedVisualizations.length > 0 && (
              <Button
                onClick={() => setShowSettingsForm(true)}
                activity="secondary"
                icon={<Icon icon="he-settings" />}
                tooltip="Settings"
                slim={true}
              />
            )}
          </div>
          <div className={listContainerClassName}>
            {!isWizardView && (
              <ListItem
                isHeader
                columns={headerColumns}
                columnWidths={colWidths}
                withSelect
              />
            )}
            <div className="custom-thin-scrollbar">
              {isWizardView && (
                <ListItem
                  isHeader
                  columns={headerColumns}
                  columnWidths={colWidths}
                  actionColumnWidth={actionColumnWidth}
                  withSelect
                />
              )}
              {filteredVisualizations.map((v) => (
                <ListItem
                  key={v.id}
                  entity={`Signal (${v.name})`}
                  item={v}
                  columns={getListItemColumns(v)}
                  columnWidths={colWidths}
                  customEdit
                  onEdit={() => setUpdating(v.id)}
                  onDelete={() => dispatch(requestDeleteVisualization(v.id))}
                  deletePermission="visualizations/Delete"
                  permissionScope={{ org: site.org, site: site.id }}
                  confirmationDialogTitle={'Delete visualization'}
                  confimationDialogBody={
                    <p style={{ paddingTop: '1.5rem' }}>
                      Are you sure you want to remove <b>{v.name}</b>?
                    </p>
                  }
                  actionColumnWidth={actionColumnWidth}
                  withSelect
                ></ListItem>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="loading-container">
          <Spinner
            size="s"
            className="spinner"
          />
        </div>
      )}

      {updating && (
        <VisualizationEditor
          visualizationId={updating}
          visualizationNames={visualizationNames}
          onClose={handleCloseVisulizationClick}
        />
      )}

      {showSettingsForm && selectedVisualizations?.length > 0 && (
        <VisualizationSettingsModal
          visLength={preFilteredVisualizations.length}
          onCloseModal={handleCloseSettingsModal}
          onSubmit={handleSubmitSettingsForm}
        ></VisualizationSettingsModal>
      )}
    </>
  );
};

export default VisualizationView;
